/**
 * Modelo de comprobante de pago / Proof of Payment
 * Referencia a un archivo de comprobante asociado a un egreso
 */
export interface ProofOfPayment {
  id: string | null; // El key del archivo S3 se persiste en este campo
  isProofOfPayment: boolean; // true = es comprobante, false = documento normal
  mailSent: boolean; // Indicador de si se ha enviado un correo relacionado con este comprobante
  uploadedBy?: string; // Usuario que subió el comprobante
  mailSentBy?: string; // Usuario que envió el correo relacionado con este comprobante
  mailSentByEmail?: string; // Correo del usuario que envió el correo relacionado con este comprobante
  mailSentAt?: string; // Fecha de envío del correo relacionado con este comprobante
  isDeleted?: boolean; // Indicador de si el comprobante ha sido eliminado
  toEmails?: string[]; //Lista de correos receptores
}

export enum EgresoEstado {
  CREADO = 'CREADO',
  EN_PROCESO = 'EN PROCESO',
  PROCESADO = 'PROCESADO',
  RECHAZADO = 'RECHAZADO',
  CONCILIADO = 'CONCILIADO',
  ELIMINADO = 'ELIMINADO',
  /** Solo para compatibilidad de lectura; no usar en nuevas escrituras. */
  CONFIRMED_LEGACY = 'CONFIRMED',
}

export enum EgresoTipoPago {
  FULL_CUENTA_BANCARIA = 'FULL CUENTA BANCARIA',
  PARCIAL = 'PARCIAL',
  FULL_SAF = 'FULL SAF',
}

/**
 * Modelo de cuenta bancaria
 * Información de la cuenta donde se efectúa el egreso
 */
export interface CuentaBancaria {
  id: string;
  nombre: string;
  tipo: string; // 'PEN' | 'USD'
  descripcion?: string;
  saldo: number;
}

/**
 * Modelo de proveedor
 * Información del proveedor relacionado al egreso
 */
export interface Proveedor {
  id: string;
  nombreProveedor: string;
  contactos?: Array<{
    nombre: string;
    cargo: string;
    correo: string;
  }>;
  correoReserva?: string;
}

/**
 * Modelo de egreso detallado
 * Contiene toda la información de un egreso incluyendo comprobantes
 */
export interface EgresoOrderReference {
  id: string;
  monto: number;
  observaciones?: string;
}

/** Campos de OS/OC agregados por el resolver; no son datos del egreso. */
export interface EgresoOrderDetail extends EgresoOrderReference {
  orderReferenceStatus?: 'RESOLVED' | 'NOT_FOUND';
  file: string;
  fileExtend?: string;
  codigo: string;
  tipoEgreso?: string;
  categoriaEgreso?: string;
  nameClient?: string;
  usuario: string;
  email: string;
  codigoReserva?: string;
  proveedorName?: string;
}

export interface EgresoSafDetail extends EgresoOrderReference {
  orderReferenceStatus?: 'RESOLVED' | 'NOT_FOUND';
  moneda: string;
}

/** Modelo de lectura enriquecido; no usar como payload de escritura. */
export interface EgresoDetalle {
  id: string;
  hasUnresolvedOrderReferences?: boolean;
  unresolvedOrderIds?: string[];
  comision: number;
  fechaPago: string | Date;
  estado: EgresoEstado;
  tipoOrden: string;
  updatedAt?: string | Date;
  fechaReprogramacion?: string | Date;
  tipoPayment?: EgresoTipoPago;
  cargadoTelecredito: boolean;

  // Relaciones
  proveedor?: Proveedor;
  cuenta?: CuentaBancaria;

  // Array de egresos individuales
  egresos?: EgresoOrderDetail[];

  // Array de egresos SAF
  egresosSAF?: EgresoSafDetail[];

  // Comprobantes de pago
  proofsOfPayment?: ProofOfPayment[];
}

export interface GetEgresoResponse {
  getEgreso: EgresoDetalle | null;
}

/**
 * Modelo de respuesta para obtener detalles de un egreso con constancias
 */
export interface EgresoDetalleConstanciaResponse {
  id: string;
  tipoOrden: string;
  proveedor?: {
    nombreProveedor: string;
    contactos?: Array<{ correo: string }>;
  };
  cuenta?: { tipo: string };
  egresos?: Array<{
    file?: string;
    codigo?: string;
    monto?: number;
    observaciones?: string;
    email?: string;
    codigoReserva?: string;
  }>;
  proofsOfPayment?: ProofOfPayment[];
}

/** Selección GraphQL de la tabla; no equivale al egreso completo. */
export interface ControlDiarioEgresoReadModel {
  id: string;
  comision?: number;
  fechaPago?: string;
  estado?: EgresoEstado;
  tipoOrden?: string;
  cargadoTelecredito?: boolean;
  proveedor?: { nombreProveedor?: string };
  cuenta?: {
    id?: string;
    nombre?: string;
    tipo?: string;
    descripcion?: string;
  };
  egresos?: EgresoOrderReference[];
  egresosSAF?: Array<EgresoOrderReference & { moneda?: string }>;
}

export interface ControlDiarioPageResponse {
  getAllEgresosTesoreriaSummaryPaginated: {
    items: ControlDiarioEgresoReadModel[];
    nextToken?: string | null;
    hasMore: boolean;
  };
}

export interface EgresoOrderDetailsResponse {
  getEgresoOrderDetails: Pick<
    EgresoDetalle,
    | 'id'
    | 'tipoOrden'
    | 'hasUnresolvedOrderReferences'
    | 'unresolvedOrderIds'
    | 'egresos'
    | 'egresosSAF'
  >;
}
